import { supabase } from '../../shared/supabase/supabaseClient';
import {
  REPORTS_REMOTE_CACHE_MS,
  reportsCacheKey,
  withReportsCache,
} from './reportsRemoteCache';
import type {
  PurchasesSummaryReport,
  PurchasesBySupplierRow,
  PurchasesByProductRow,
} from '../domain/reportsPurchasesTypes';
import type { SupplierPerformanceRow } from '../domain/reportsSupplierPerformanceTypes';

function mapBySupplier(rows: unknown): PurchasesBySupplierRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((r: Record<string, unknown>) => ({
    supplierId: r.supplierId != null ? String(r.supplierId) : null,
    supplierName: String(r.supplierName ?? 'Sin proveedor'),
    total: Number(r.total ?? 0),
    receptionCount: Number(r.receptionCount ?? 0),
  }));
}

function mapByProduct(rows: unknown): PurchasesByProductRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((r: Record<string, unknown>) => {
    const quantity = Number(r.quantity ?? 0);
    const totalCost = Number(r.totalCost ?? 0);
    const receivedUnitCost =
      r.receivedUnitCost != null
        ? Number(r.receivedUnitCost)
        : quantity > 0
          ? totalCost / quantity
          : null;
    return {
      productId: r.productId != null ? String(r.productId) : null,
      productName: String(r.productName ?? 'Sin nombre'),
      quantity,
      totalCost,
      avgUnitCost: r.avgUnitCost != null ? Number(r.avgUnitCost) : null,
      orderedUnitCost: r.orderedUnitCost != null ? Number(r.orderedUnitCost) : null,
      receivedUnitCost,
    };
  });
}

export async function fetchRemotePurchasesSummary(
  branchId: string,
  fromIso: string,
  toIso: string,
): Promise<PurchasesSummaryReport | null> {
  return withReportsCache(
    reportsCacheKey(['reports_branch_purchases_summary', branchId, fromIso, toIso]),
    async () => {
      const { data, error } = await supabase.schema('pos').rpc('reports_branch_purchases_summary', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
      });
      if (error || !data) return null;
      const j = data as Record<string, unknown>;
      return {
        totalPurchased: Number(j.totalPurchased ?? 0),
        totalOrders: Number(j.totalOrders ?? 0),
        totalReceptions: Number(j.totalReceptions ?? 0),
        totalReturns: Number(j.totalReturns ?? 0),
        totalExpenses: Number(j.totalExpenses ?? 0),
        netPurchased: Number(j.netPurchased ?? 0),
        bySupplier: mapBySupplier(j.bySupplier),
        byProduct: mapByProduct(j.byProduct),
      };
    },
    undefined,
    { category: 'reports', name: 'reports_branch_purchases_summary' },
  );
}

export { REPORTS_REMOTE_CACHE_MS };

function mapSupplierPerformance(rows: unknown): SupplierPerformanceRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((r: Record<string, unknown>) => ({
    supplierId: r.supplierId != null ? String(r.supplierId) : null,
    supplierName: String(r.supplierName ?? 'Sin proveedor'),
    totalOrders: Number(r.totalOrders ?? 0),
    receivedOrders: Number(r.receivedOrders ?? 0),
    partialOrders: Number(r.partialOrders ?? 0),
    cancelledOrders: Number(r.cancelledOrders ?? 0),
    fulfillmentRate: Number(r.fulfillmentRate ?? 0),
    avgDeliveryDays: r.avgDeliveryDays != null ? Number(r.avgDeliveryDays) : null,
    totalPurchased: Number(r.totalPurchased ?? 0),
  }));
}

export async function fetchRemoteSupplierPerformance(
  branchId: string,
  fromIso: string,
  toIso: string,
): Promise<SupplierPerformanceRow[] | null> {
  return withReportsCache(
    reportsCacheKey(['reports_branch_supplier_performance', branchId, fromIso, toIso]),
    async () => {
      const { data, error } = await supabase.schema('pos').rpc('reports_branch_supplier_performance', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
      });
      if (error || !data) return null;
      return mapSupplierPerformance(data);
    },
    undefined,
    { category: 'reports', name: 'reports_branch_supplier_performance' },
  );
}
